import { X, Plus } from "lucide-react";
import { Button } from "@/design-system/components/Button/Button";
import { colors, TextField } from "@material-ui/core";

import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";
import clsx from "clsx";

import { ConfigureDestinationsModal } from "../../modals/ConfigureDestinationsModal";
import { KeyValueMappingControl } from "../../modals/ConfigureIntegrationModal";
import { DestinationConfigRow } from "../shared/ConfigPageRow";
import { useModalStyles } from "../../modals/styles";

const WebhooksEndpointListConfigurationControl = ({
  field,
  value = [],
  onChange,
}) => {
  const classes = useModalStyles();

  const onWebhookChange = (index, newWebhook) => {
    const newValue = value.slice();
    newValue[index] = newWebhook;
    onChange(field.key, newValue);
  };

  const onDelete = (index) => {
    const newValue = value.slice();
    newValue.splice(index, 1);
    onChange(field.key, newValue);
  };

  return (
    <div className="justify-items-stretch" style={{ width: "100%" }}>
      {value.map((webhook, index) => {
        return (
          <div className="flex items-stretch" key={index}>
            <WebhookEndpointConfigurationControl
              field={field}
              webhook={webhook}
              onWebhookChange={(newWebhook) =>
                onWebhookChange(index, newWebhook)
              }
            />
            <span className="flex flex-wrap justify-end">
              <Tooltip content="Delete Webhook">
                <div
                  className="hover:bg-navy-200 flex items-center justify-center rounded-full p-1"
                  onClick={() => onDelete(index)}
                >
                  <X className={classes.closeIcon} />
                </div>
              </Tooltip>
            </span>
          </div>
        );
      })}
      <Button
        onClick={() => onChange(field.key, [...value, { key: "", value: "" }])}
        className="bg-navy-100 text-navy-800 hover:bg-navy-200 inline-flex cursor-pointer items-center rounded px-3 py-1 text-xs"
        trailingIcon={<Plus />}
      >
        Add Webhook
      </Button>
    </div>
  );
};

const WebhookEndpointConfigurationControl = ({
  field,
  webhook,
  onWebhookChange,
}) => {
  const classes = useModalStyles();

  const handleWebhookChange = (webhookChanges) => {
    const newWebhook = { ...webhook, ...webhookChanges };
    onWebhookChange(newWebhook);
  };

  return (
    <div className="justify-items-stretch" style={{ width: "100%" }}>
      <TextField
        fullWidth
        autoComplete="off"
        label="Webhook URL"
        name="Webhook URL"
        onChange={(event) =>
          handleWebhookChange({ endpoint: event.target.value })
        }
        value={webhook.endpoint || ""}
        key={field.name}
        variant="outlined"
        type="text"
        inputProps={{ style: { fontFamily: "monospace" } }}
        className={clsx(classes.inputField, "fs-exclude mb-2")}
      />
      <KeyValueMappingControl
        field={field}
        value={webhook.headers || []}
        onChange={(fieldKey, newMapping) =>
          handleWebhookChange({ headers: newMapping })
        }
        keyLabel="Header name"
        valueLabel="Header value"
        objectLabel="Header"
      />
    </div>
  );
};

const WebhooksModal = ({
  destination,
  open,
  onClose,
  handleUpdateConfigureDest,
  existingConfig,
}) => {
  const settings = existingConfig?.settings || {};

  return (
    <ConfigureDestinationsModal
      settingsToControl={{
        webhooks: WebhooksEndpointListConfigurationControl,
      }}
      limitSettings={["webhooks"]}
      open={open}
      settings={settings}
      dest={destination}
      onClose={onClose}
      onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
      existingConfig={existingConfig}
    />
  );
};

const WebhooksRow = ({ setConfigModalOpen, existingConfig }) => {
  const settings = existingConfig?.settings || {};

  const webhooksSet =
    settings.webhooks &&
    settings.webhooks.length > 0 &&
    settings.webhooks[0].endpoint;

  return (
    <DestinationConfigRow
      name="Webhooks"
      textColor={webhooksSet ? "" : colors.red[600]}
      labelText={webhooksSet ? "Configured" : "Not configured"}
      onOpenConfigModal={() => setConfigModalOpen("configuration")}
    />
  );
};

const ConfigurationRow = ({ setConfigModalOpen, existingConfig }) => {
  const settings = existingConfig?.settings || {};

  const webhooksSet =
    settings.webhooks &&
    settings.webhooks.length > 0 &&
    settings.webhooks[0].endpoint;

  return (
    <DestinationConfigRow
      disabled={!webhooksSet}
      name="Options"
      labelText="Optional"
      onOpenConfigModal={() => {
        if (webhooksSet) {
          setConfigModalOpen();
        }
      }}
    />
  );
};

const ConfigurationModal = ({
  destination,
  open,
  onClose,
  handleUpdateConfigureDest,
  existingConfig,
}) => {
  const settings = existingConfig?.settings || {};

  const limitSettings = ["enableIdentifyEvents", "enablePageviewEvents"];

  return (
    <ConfigureDestinationsModal
      limitSettings={limitSettings}
      open={open}
      settings={settings}
      dest={destination}
      onClose={onClose}
      onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
      existingConfig={existingConfig}
    />
  );
};

const WEBHOOKS = "WEBHOOKS";
const OPTIONS = "OPTIONS";

const Rows = {
  WEBHOOKS: WebhooksRow,
  OPTIONS: ConfigurationRow,
};

const Modals = {
  WEBHOOKS: WebhooksModal,
  OPTIONS: ConfigurationModal,
};

const RowOrder = [WEBHOOKS, OPTIONS];

export default {
  Rows,
  RowOrder,
  Modals,
};
